import type { EstadoRevision, Etapa } from "./dominio.js";

/**
 * Máquina de estados de una revisión. Es la transcripción de
 * docs/diagramas/02-ciclo-de-vida.mmd; si cambia uno, cambia el otro.
 *
 * "Abrir la revisión siguiente" no está acá porque no cambia esta revisión: crea otra.
 */
const ACCIONES_REVISION = [
  "enviar_a_revision",
  /**
   * Ruta corta (2026-10-29): en un tipo sin revisor, quien elabora firma y la revisión pasa
   * directo a esperar la aprobación.
   */
  "enviar_a_aprobacion",
  "devolver",
  "firmar_revision",
  "firmar_aprobacion",
  /** Fase 3b: un documento informativo se publica sin ruta de firmas (borrador → vigente). */
  "publicar",
  "declarar_obsoleta",
  "aplicar_disposicion",
] as const;
export type AccionRevision = (typeof ACCIONES_REVISION)[number];

type Transicion = { desde: EstadoRevision; accion: AccionRevision; hacia: EstadoRevision };

const TRANSICIONES: readonly Transicion[] = [
  { desde: "borrador", accion: "enviar_a_revision", hacia: "en_revision" },
  { desde: "borrador", accion: "enviar_a_aprobacion", hacia: "esperando_aprobacion" },
  { desde: "en_revision", accion: "devolver", hacia: "borrador" },
  { desde: "en_revision", accion: "firmar_revision", hacia: "esperando_aprobacion" },
  { desde: "esperando_aprobacion", accion: "devolver", hacia: "borrador" },
  { desde: "esperando_aprobacion", accion: "firmar_aprobacion", hacia: "vigente" },
  { desde: "borrador", accion: "publicar", hacia: "vigente" },
  { desde: "vigente", accion: "declarar_obsoleta", hacia: "obsoleta" },
  { desde: "vigente", accion: "aplicar_disposicion", hacia: "dispuesto" },
];

export class TransicionInvalida extends Error {
  constructor(
    readonly desde: EstadoRevision,
    readonly accion: AccionRevision,
  ) {
    super(`No se puede "${accion}" desde el estado "${desde}"`);
    this.name = "TransicionInvalida";
  }
}

/** Devuelve el estado siguiente o lanza TransicionInvalida. */
export function transicionar(desde: EstadoRevision, accion: AccionRevision): EstadoRevision {
  const t = TRANSICIONES.find((x) => x.desde === desde && x.accion === accion);
  if (!t) throw new TransicionInvalida(desde, accion);
  return t.hacia;
}

/** Estados en los que la revisión ya no se puede editar (ADR-06: se protege también en la base). */
const ESTADOS_INMUTABLES: readonly EstadoRevision[] = ["vigente", "obsoleta", "dispuesto"];

export function esInmutable(estado: EstadoRevision): boolean {
  return ESTADOS_INMUTABLES.includes(estado);
}

/** Qué etapa de la ruta tiene la pelota en cada estado; null si nadie debe firmar. */
export function etapaQueFirma(estado: EstadoRevision): Etapa | null {
  switch (estado) {
    case "borrador":
      return "elaboro";
    case "en_revision":
      return "reviso";
    case "esperando_aprobacion":
      return "aprobo";
    default:
      return null;
  }
}

/** Estados en los que la revisión está en camino y alguien tiene que firmar. */
export const ESTADOS_EN_CURSO: readonly EstadoRevision[] = [
  "borrador",
  "en_revision",
  "esperando_aprobacion",
];

/**
 * La regla única de "te toca firmar", la misma para el contador del sidebar, la bandeja de
 * pendientes y el indicador "Esperan tu firma" de la lista maestra: la persona está asignada a
 * esa etapa y la revisión está justo en esa etapa. Incluye el borrador: a quien elabora le toca
 * firmar y enviar, y es donde vuelve una revisión devuelta.
 */
export function leTocaFirmar(estado: EstadoRevision, etapa: Etapa): boolean {
  return etapaQueFirma(estado) === etapa;
}
